import { Capacitor } from "@capacitor/core";
import { APP_ID, getAuthPlatform } from "./nativeAuth";

/**
 * Native deep links for auth (audit H4).
 *
 * Magic links (and any Supabase redirect) sent from the native app point at
 * the custom URL scheme `fr.batchcooking.app://auth/callback`, registered in
 * `AndroidManifest.xml` (intent-filter) and `Info.plist` (CFBundleURLTypes).
 * When the OS hands such a URL to the app, `@capacitor/app` emits
 * `appUrlOpen`; we route it to `/auth/callback` with its query / fragment
 * intact so `AuthCallback` can finish the sign-in.
 *
 * The scheme must be in Supabase → Authentication → URL Configuration →
 * Redirect URLs (see docs/native-auth-setup.md), or Supabase falls back to
 * the Site URL.
 */
export const AUTH_URL_SCHEME = APP_ID;
export const NATIVE_AUTH_CALLBACK_URL = `${AUTH_URL_SCHEME}://auth/callback`;

const CALLBACK_PATH = "/auth/callback";

/**
 * Where Supabase should send the user back after e-mail / OAuth auth:
 * the custom scheme on native, `<webOrigin>/auth/callback` on the web.
 */
export function authCallbackUrl(webOrigin: string): string {
  return getAuthPlatform() === "web"
    ? `${webOrigin}${CALLBACK_PATH}`
    : NATIVE_AUTH_CALLBACK_URL;
}

/**
 * Map a URL opened by the OS to an in-app path. Only our scheme's auth
 * callback is accepted; anything else returns null and is ignored.
 */
export function deepLinkToPath(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.protocol !== `${AUTH_URL_SCHEME}:`) return null;
  // `fr.batchcooking.app://auth/callback` → host "auth", pathname "/callback".
  const path = `/${parsed.host}${parsed.pathname}`.replace(/\/+$/, "");
  if (path !== CALLBACK_PATH) return null;
  return `${path}${parsed.search}${parsed.hash}`;
}

/**
 * Listen for auth deep links (warm `appUrlOpen` and the cold-start launch
 * URL) and hand the in-app path to `navigate`. No-op on the web. Resolves
 * to an unregister function.
 */
export async function registerDeepLinks(
  navigate: (path: string) => void,
): Promise<() => void> {
  if (!Capacitor.isNativePlatform()) return () => {};
  const { App } = await import("@capacitor/app");

  // A cold start can deliver the same URL via getLaunchUrl() and appUrlOpen;
  // the code / token_hash in it is single-use, so route it only once.
  let lastUrl: string | null = null;
  const handle = (url: string) => {
    const path = deepLinkToPath(url);
    if (!path || url === lastUrl) return;
    lastUrl = url;
    navigate(path);
  };

  const listener = await App.addListener("appUrlOpen", ({ url }) =>
    handle(url),
  );
  const launch = await App.getLaunchUrl();
  if (launch?.url) handle(launch.url);

  return () => {
    void listener.remove();
  };
}
